import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Download,
  KeyRound,
  Minus,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { useTranslation } from '@/i18n';
import { useExcelIo } from '@/hooks/useExcelIo';
import { useErrorTranslator } from '@/errors/translate';
import { hasUnknownOutcomes, type ExcelEntity } from '@/utils/excelIo';
import type {
  ImportCredential,
  ImportFieldChange,
  ImportResult,
  ImportRowOutcome,
  ImportRowResult,
} from '@/types/excelImport';
import { Badge } from './Badge';
import { Button } from './Button';
import { FileUpload } from './FileUpload';
import { Modal } from './Modal';
import { Tooltip } from './Tooltip';

interface ExcelImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  entity: ExcelEntity;
  /** Sheet title, e.g. "Kunden importieren". */
  title: string;
  /** Called after a committed import so the list can refresh. */
  onImported: () => void;
}

type Step = 'select' | 'preview' | 'done';
type OutcomeFilter = ImportRowOutcome | 'All';

const OUTCOME_ORDER: ImportRowOutcome[] = ['Failed', 'Created', 'Updated', 'Skipped'];

const OUTCOME_BADGE: Record<ImportRowOutcome, 'success' | 'info' | 'default' | 'danger'> = {
  Created: 'success',
  Updated: 'info',
  Skipped: 'default',
  Failed: 'danger',
};

const OUTCOME_ICON: Record<ImportRowOutcome, typeof Plus> = {
  Created: Plus,
  Updated: RefreshCw,
  Skipped: Minus,
  Failed: AlertTriangle,
};

/**
 * Header + rows share this template:
 * checkbox | row number | record | outcome | reason/changes | expander chevron.
 */
const ROW_GRID =
  'grid grid-cols-[24px_48px_minmax(0,1.6fr)_104px_minmax(0,1.2fr)_24px] gap-3 px-3 py-2';

const CHECKBOX_CLASSES =
  'h-4 w-4 rounded border-slate-300 text-[#00A871] focus:ring-2 focus:ring-[#00A871]/30';

/** Same key format the commit sends as an `excludeRows` token. */
const rowKey = (row: ImportRowResult): string => `${row.sheet}:${row.row}`;

/** Failed and Skipped rows are never written — only these two outcomes are a choice. */
const isSelectable = (row: ImportRowResult): boolean =>
  row.outcome === 'Created' || row.outcome === 'Updated';

const toggleInSet = (set: Set<string>, key: string): Set<string> => {
  const next = new Set(set);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  return next;
};

/**
 * Renders one outcome badge, tolerating a value the client does not know.
 *
 * A report row is diagnostic output — an unexpected `outcome` must degrade to a plain
 * label, never take down the modal with it. Indexing the icon map directly used to
 * yield `undefined` and crash the whole page through the error boundary.
 */
function OutcomeBadge({ outcome, label }: { outcome: ImportRowOutcome; label: string }) {
  const Icon = OUTCOME_ICON[outcome];

  return (
    <Badge variant={OUTCOME_BADGE[outcome] ?? 'default'}>
      {Icon && <Icon className="w-3 h-3 mr-1" strokeWidth={2} />}
      {label}
    </Badge>
  );
}

/**
 * Upload → preview → confirm flow for the Excel round-trip.
 *
 * The preview is a real server-side dry run, so what the user approves is exactly what
 * the second call writes. No stepper component exists in the design system, so the three
 * steps are internal state of a single modal.
 */
export function ExcelImportModal({
  isOpen,
  onClose,
  entity,
  title,
  onImported,
}: ExcelImportModalProps) {
  const { t } = useTranslation(['common', 'errors']);
  const translateError = useErrorTranslator();
  const { importing, previewImport, commitImport } = useExcelIo(entity);

  const [step, setStep] = useState<Step>('select');
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [filter, setFilter] = useState<OutcomeFilter>('All');
  // Both keyed `"{sheet}:{row}"` — the same token the commit sends as `excludeRows`.
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // A failed commit leaves the modal on the preview step; the toast alone is easy to
  // miss and the user then believes the import silently did nothing.
  const [commitFailed, setCommitFailed] = useState(false);

  const file = files[0];
  const importable = result ? result.created + result.updated : 0;
  // `deselected` only ever holds selectable rows, so plain subtraction is exact.
  const selectedCount = importable - deselected.size;
  // Version skew: an outcome the client cannot read makes every filter come up empty.
  // Say so, rather than leaving the user staring at chips that seem broken.
  const unknownOutcomes = result ? hasUnknownOutcomes(result.rows) : false;

  const visibleRows = useMemo(() => {
    if (!result) return [];
    const rows = filter === 'All' ? result.rows : result.rows.filter((r) => r.outcome === filter);
    // Failures first — they are what the user has to act on.
    return [...rows].sort(
      (a, b) => OUTCOME_ORDER.indexOf(a.outcome) - OUTCOME_ORDER.indexOf(b.outcome) || a.row - b.row,
    );
  }, [result, filter]);

  // The select-all checkbox operates on what the current filter shows, not the whole file.
  const selectableVisible = visibleRows.filter(isSelectable);
  const selectedVisibleCount = selectableVisible.filter((r) => !deselected.has(rowKey(r))).length;
  const allVisibleSelected =
    selectableVisible.length > 0 && selectedVisibleCount === selectableVisible.length;

  const toggleAllVisible = () => {
    setDeselected((prev) => {
      const next = new Set(prev);
      for (const row of selectableVisible) {
        if (allVisibleSelected) {
          next.add(rowKey(row));
        } else {
          next.delete(rowKey(row));
        }
      }
      return next;
    });
  };

  const reset = () => {
    setStep('select');
    setFiles([]);
    setResult(null);
    setFilter('All');
    setDeselected(new Set());
    setExpanded(new Set());
    setCommitFailed(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handlePreview = async () => {
    if (!file) return;
    const preview = await previewImport(file);
    if (preview) {
      setResult(preview);
      setFilter('All');
      setDeselected(new Set());
      setExpanded(new Set());
      setCommitFailed(false);
      setStep('preview');
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    const applied = await commitImport(file, [...deselected]);
    if (applied) {
      setCommitFailed(false);
      setResult(applied);
      setFilter('All');
      setStep('done');
      onImported();
    } else {
      setCommitFailed(true);
    }
  };

  const rowReason = (row: ImportRowResult): string | null => {
    // Skipped rows may carry a reason too (commit report: `imports.rowDeselected`).
    if (row.outcome !== 'Failed' && !row.reasonCode) return null;

    // translateError already falls back to the server message, then to a generic code.
    const reason = row.reasonCode
      ? translateError({ code: row.reasonCode, message: row.reasonMessage ?? '' })
      : (row.reasonMessage ?? t('common:excelImport.unknownReason'));

    return row.column ? `${row.column}: ${reason}` : reason;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="xl"
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={importing}>
            {step === 'done' ? t('common:actions.close') : t('common:actions.cancel')}
          </Button>

          {step === 'select' && (
            <Button onClick={handlePreview} disabled={!file} loading={importing}>
              {importing ? t('common:excelImport.checking') : t('common:excelImport.check')}
            </Button>
          )}

          {step === 'preview' && (
            <Button onClick={handleCommit} disabled={selectedCount === 0} loading={importing}>
              {importing
                ? t('common:excelImport.importing')
                : t('common:excelImport.confirm', { total: selectedCount })}
            </Button>
          )}
        </>
      }
    >
      {step === 'select' && (
        <div className="space-y-4">
          <p className="text-[13px] text-slate-600">{t('common:excelImport.intro')}</p>

          <FileUpload
            label={t('common:excelImport.fileLabel')}
            accept=".xlsx,.xlsm"
            files={files}
            onChange={setFiles}
            helperText={t('common:excelImport.fileHint')}
          />
        </div>
      )}

      {(step === 'preview' || step === 'done') && result && (
        <div className="space-y-4">
          <div
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px] ${
              step === 'done'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-slate-200 bg-slate-50 text-slate-600'
            }`}
          >
            {step === 'done' ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
            ) : (
              <Download className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
            )}
            <span>
              {step === 'done'
                ? t('common:excelImport.doneHint')
                : t('common:excelImport.previewHint')}
            </span>
          </div>

          {step === 'preview' && commitFailed && (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
              <span>{t('common:excelImport.messages.commitFailed')}</span>
            </div>
          )}

          {result.credentials.length > 0 && (
            <CredentialsPanel credentials={result.credentials} />
          )}

          {unknownOutcomes && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
              <span>{t('common:excelImport.staleBackend')}</span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <SummaryChip
              label={t('common:excelImport.filters.all')}
              count={result.totalRows}
              active={filter === 'All'}
              onClick={() => setFilter('All')}
            />
            {OUTCOME_ORDER.map((outcome) => (
              <SummaryChip
                key={outcome}
                label={t(`common:excelImport.outcomes.${outcome}`)}
                count={countOf(result, outcome)}
                active={filter === outcome}
                outcome={outcome}
                onClick={() => setFilter(outcome)}
                title={t(`common:excelImport.help.${outcome}`)}
              />
            ))}
          </div>

          {/* What the four results actually mean — the counts alone are not self-explanatory. */}
          <dl className="grid gap-x-6 gap-y-1 sm:grid-cols-2 text-[11px] text-slate-500">
            {OUTCOME_ORDER.map((outcome) => (
              <div key={outcome} className="flex gap-1.5">
                <dt className="font-medium text-slate-600 shrink-0">
                  {t(`common:excelImport.outcomes.${outcome}`)}
                </dt>
                <dd>{t(`common:excelImport.help.${outcome}`)}</dd>
              </div>
            ))}
          </dl>

          {step === 'preview' && deselected.size > 0 && (
            <div className="space-y-1 text-[12px] text-slate-600">
              <p>{t('common:excelImport.deselectedHint', { count: deselected.size })}</p>
              {entity === 'tasks' && <p>{t('common:excelImport.deselectHintTasks')}</p>}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 overflow-hidden">
            <div
              className={`${ROW_GRID} items-center bg-slate-50 border-b border-slate-200 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400`}
            >
              {step === 'preview' ? (
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  disabled={selectableVisible.length === 0}
                  onChange={toggleAllVisible}
                  ref={(el) => {
                    if (el) {
                      el.indeterminate =
                        selectedVisibleCount > 0 && selectedVisibleCount < selectableVisible.length;
                    }
                  }}
                  aria-label={t('common:excelImport.selectAll')}
                  className={CHECKBOX_CLASSES}
                />
              ) : (
                <span />
              )}
              <span title={t('common:excelImport.columns.rowHint')}>
                {t('common:excelImport.columns.row')}
              </span>
              <span>{t('common:excelImport.columns.record')}</span>
              <span>{t('common:excelImport.columns.outcome')}</span>
              <span>{t('common:excelImport.columns.reason')}</span>
              <span />
            </div>

            <div className="max-h-[42vh] overflow-y-auto">
              {visibleRows.length === 0 ? (
                <p className="px-3 py-6 text-center text-[12px] text-slate-500">
                  {filter === 'All'
                    ? t('common:excelImport.noRows')
                    : t('common:excelImport.noRowsForFilter')}
                </p>
              ) : (
                visibleRows.map((row) => {
                  const key = rowKey(row);
                  const isDeselected = deselected.has(key);
                  const changes =
                    row.outcome === 'Updated' && row.changes?.length ? row.changes : null;
                  const isExpanded = changes !== null && expanded.has(key);

                  return (
                    <div key={key} className="border-b border-slate-100 last:border-b-0">
                      <div className={`${ROW_GRID} items-center ${isDeselected ? 'opacity-60' : ''}`}>
                        {step === 'preview' && isSelectable(row) ? (
                          <input
                            type="checkbox"
                            checked={!isDeselected}
                            onChange={() => setDeselected((prev) => toggleInSet(prev, key))}
                            aria-label={`${t('common:excelImport.selectRow')} ${row.row}`}
                            className={CHECKBOX_CLASSES}
                          />
                        ) : (
                          <span />
                        )}
                        <span className="text-[12px] text-slate-500 tabular-nums">{row.row}</span>
                        {row.label ? (
                          <Tooltip portal content={row.label} className="min-w-0">
                            <span className="text-[13px] text-slate-900 truncate">{row.label}</span>
                          </Tooltip>
                        ) : (
                          <span className="text-[13px] text-slate-900">—</span>
                        )}
                        <OutcomeBadge
                          outcome={row.outcome}
                          label={t(`common:excelImport.outcomes.${row.outcome}`, {
                            defaultValue: row.outcome,
                          })}
                        />
                        <span className="text-[12px] text-slate-600 truncate">
                          {changes
                            ? `${t('common:excelImport.changes.count', { count: changes.length })} — ${changes
                                .map((change) => change.field)
                                .join(', ')}`
                            : (rowReason(row) ?? '—')}
                        </span>
                        {changes ? (
                          <button
                            type="button"
                            onClick={() => setExpanded((prev) => toggleInSet(prev, key))}
                            aria-label={t(
                              isExpanded
                                ? 'common:excelImport.changes.hide'
                                : 'common:excelImport.changes.show',
                            )}
                            className="rounded-md p-1 text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4" strokeWidth={2} />
                            ) : (
                              <ChevronDown className="w-4 h-4" strokeWidth={2} />
                            )}
                          </button>
                        ) : (
                          <span />
                        )}
                      </div>

                      {isExpanded && changes && <ChangesPanel changes={changes} />}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

function countOf(result: ImportResult, outcome: ImportRowOutcome): number {
  switch (outcome) {
    case 'Created':
      return result.created;
    case 'Updated':
      return result.updated;
    case 'Skipped':
      return result.skipped;
    case 'Failed':
      return result.failed;
  }
}

/**
 * Field-level diff of one Updated row: German column header, old value struck through,
 * new value. Values arrive as formatted cell text, `—` marks an empty side.
 */
function ChangesPanel({ changes }: { changes: ImportFieldChange[] }) {
  return (
    <div className="bg-slate-50 px-3 py-2 space-y-1">
      {changes.map((change) => (
        <div
          key={change.field}
          className="grid grid-cols-[minmax(0,160px)_minmax(0,1fr)] gap-3 text-[12px]"
        >
          <span className="text-slate-500 truncate">{change.field}</span>
          <span className="min-w-0 break-words">
            {change.oldValue ? (
              <span className="text-slate-400 line-through">{change.oldValue}</span>
            ) : (
              <span className="text-slate-400">—</span>
            )}
            <span className="text-slate-400"> → </span>
            <span className="text-slate-900">{change.newValue || '—'}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * One-time passwords of accounts the import created.
 *
 * These exist only in this response — there is no second chance to read them, which is
 * why the panel is prominent and offers a download. Rendered outside the row table so
 * secrets never end up in the filtered, sorted diagnostic list.
 */
function CredentialsPanel({ credentials }: { credentials: ImportCredential[] }) {
  const { t } = useTranslation('common');

  const download = () => {
    const header = [
      t('common:excelImport.credentials.personalNumber'),
      t('common:excelImport.credentials.name'),
      t('common:excelImport.credentials.password'),
    ];
    const rows = credentials.map((c) => [c.personalNumber, c.name, c.initialPassword]);
    // Semicolon-separated: what Excel expects in a German locale.
    const csv = [header, ...rows]
      .map((cells) => cells.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');

    // Leading BOM so Excel reads the umlauts as UTF-8 rather than as Latin-1.
    const url = URL.createObjectURL(
      new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = t('common:excelImport.credentials.fileName');
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
      <div className="flex items-start gap-2 text-[12px] text-amber-900">
        <KeyRound className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
        <span>{t('common:excelImport.credentials.hint', { count: credentials.length })}</span>
      </div>

      <div className="max-h-40 overflow-y-auto rounded-md border border-amber-200 bg-white">
        {credentials.map((credential) => (
          <div
            key={credential.personalNumber}
            className="grid grid-cols-[110px_minmax(0,1fr)_minmax(0,1fr)] gap-3 px-3 py-1.5 border-b border-amber-100 last:border-b-0 text-[12px]"
          >
            <span className="font-mono tabular-nums text-slate-600">{credential.personalNumber}</span>
            <span className="text-slate-900 truncate">{credential.name}</span>
            <span className="font-mono text-slate-900 select-all">{credential.initialPassword}</span>
          </div>
        ))}
      </div>

      <Button variant="secondary" size="sm" onClick={download} leftIcon={<Download className="w-3.5 h-3.5" strokeWidth={2} />}>
        {t('common:excelImport.credentials.download')}
      </Button>
    </div>
  );
}

interface SummaryChipProps {
  label: string;
  count: number;
  active: boolean;
  outcome?: ImportRowOutcome;
  title?: string;
  onClick: () => void;
}

function SummaryChip({ label, count, active, outcome, title, onClick }: SummaryChipProps) {
  const tone =
    outcome === 'Failed' && count > 0 ? 'text-red-600' : active ? 'text-slate-900' : 'text-slate-600';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[12px] ${
        active ? 'border-slate-300 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'
      } ${tone}`}
    >
      {label}
      <span className="font-mono tabular-nums text-[11px]">{count}</span>
    </button>
  );
}

export default ExcelImportModal;
